import React, { useEffect } from 'react';
import { Form, Formik, FormikHelpers } from 'formik';
import merge from 'lodash.merge';

import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';

import Grid from '@material-ui/core/Grid';
import Dialog from '@material-ui/core/Dialog/Dialog';
import DialogContent from '@material-ui/core/DialogContent/DialogContent';
import DialogActions from '@material-ui/core/DialogActions/DialogActions';
import Button from '@material-ui/core/Button/Button';

import compose from 'helpers/compose';
import FormTextField from 'components/FormFields/FormTextField';
import FormNumberField from 'components/FormFields/FormNumberField';
import GridSubtitle from 'components/GridSubtitle/GridSubtitle';

import ControlButtons from 'components/ControlButtons';
import FormRadioGroup from 'components/FormRadioGroup';
import DialogHeader from 'components/DialogHeader/DialogHeader';
import withoutEnterSubmit from 'components/withoutEnterSubmit';
import withInfo from 'components/Tooltip/withInfo';

import { Section } from './PortfolioModal';
import { Currency } from 'enums';
import { AdminPortfoliosStore, initialValues, Portfolio } from 'stores/admin/adminPortfolios';

const TooltipButton = withInfo(Button);
const FormWithoutEnterSubmit = withoutEnterSubmit(Form);

interface Props {
  handleClose: () => void;
  portfolioId?: string;
  clone?: boolean;
  store: AdminPortfoliosStore;
  setSection: (section: Section) => void;
  setId: (id: string) => void;
}

const ExternalProductModalSetup: React.FC<Props> = ({ portfolioId, store, handleClose, setSection, setId, clone }) => {
  const { t } = useTranslation();

  useEffect(() => {
    (async () => {
      if (portfolioId) {
        await store.fetchPortfolio(portfolioId, clone);
      }
    })();
  }, [portfolioId, clone, store]);

  const { data } = store;

  const handleSubmit = (values: Portfolio, formikHelpers: FormikHelpers<Portfolio>) => {
    store.handleNext(values, formikHelpers, setSection, setId);
  };

  return (
    <Formik
      initialValues={data ? merge({}, initialValues, data) : initialValues}
      onSubmit={handleSubmit}
      enableReinitialize
    >
      {(formikProps) => (
        <FormWithoutEnterSubmit id={'portfolio-details'}>
          <Dialog maxWidth="md" open={true}>
            <DialogHeader onClose={handleClose}>{t(`portfolio_DETAILS`)}</DialogHeader>
            <DialogContent>
              <Grid container alignItems="center">
                <Grid item xs={12}>
                  <FormTextField name="title" label={t('portfolio_detail_title')} />
                </Grid>

                <Grid item xs={4}>
                  <FormRadioGroup
                    name="currency"
                    label={t('general_currency')}
                    formikProps={formikProps}
                    horizontal
                    disabled={!!portfolioId}
                    values={[
                      {
                        value: 'CZK',
                        label: 'CZK',
                      },
                      {
                        value: 'EUR',
                        label: 'EUR',
                      },
                    ]}
                    afterChange={() => {
                      if (formikProps.values.currency === Currency.EUR) {
                        formikProps.setFieldValue('hedgingUsd', 0);
                        formikProps.setFieldValue('hedgingEur', 0);
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={8} />

                <Grid item xs={12}>
                  <FormTextField
                    name="description"
                    multiline={true}
                    minRows={6}
                    maxLength={500}
                    label={t('portfolio_detail_description')}
                  />
                </Grid>

                <Grid item xs={12}>
                  <FormTextField name="key" label={t('portfolio_detail_key')} disabled={!!portfolioId && !clone} />
                </Grid>

                <Grid item xs={2}>
                  <GridSubtitle label>{t('general_Hedging')}</GridSubtitle>
                </Grid>

                <Grid item xs={2}>
                  <FormNumberField
                    name="hedgingUsd"
                    endAdornment={'%'}
                    disabled={formikProps.values.currency === Currency.EUR}
                    fast={false}
                  />
                </Grid>
                <Grid item xs={1}>
                  USD
                </Grid>

                <Grid item xs={2}>
                  <FormNumberField
                    name="hedgingEur"
                    endAdornment={'%'}
                    disabled={formikProps.values.currency === Currency.EUR}
                    fast={false}
                  />
                </Grid>
                <Grid item xs={1}>
                  EUR
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <ControlButtons
                isSubmitting={formikProps.isSubmitting}
                backText={t('general_cancel')}
                onBack={handleClose}
                form={'portfolio-details'}
                nextText={t('general_continue')}
              >
                <TooltipButton
                  type={'button'}
                  form={'portfolio-details'}
                  onClick={() => store.handleNext(formikProps.values, formikProps)}
                  variant="contained"
                  color="primary"
                  id={'save-form-button'}
                  disabled={formikProps.isSubmitting}
                >
                  {t('general_save')}
                </TooltipButton>
              </ControlButtons>
            </DialogActions>
          </Dialog>
        </FormWithoutEnterSubmit>
      )}
    </Formik>
  );
};

export default compose(observer)(ExternalProductModalSetup);
